/**
 * This component is used to show what we've called budget settings.  We're now migrating to the project/wallet paradigm.
 * Once that migration is complete, this component will be deprecated.
 * 
 * For resellers that can selectively use "budgets", this component supports turning budgets on and off.
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import AbEnvironment from '../auth/AbEnvironment'; 

import PropTypes from 'prop-types';
import { Button, Grid, Paper, Switch, Tooltip, Typography } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import HelpOutline from '@mui/icons-material/HelpOutline';
import { getValueForRender } from '../../utils/InputUtils';
import { NumericFormat } from 'react-number-format';
import SpendingWidgets from '../home/SpendingWidgets';
import SettingsBackupRestore from '@mui/icons-material/SettingsBackupRestore';

import ServerError from '../ui/ServerError';
import { NumericFormat as NumberFormat } from 'react-number-format';

import LoadingSpinner from '../ui/LoadingSpinner';
import TopUps from '../account/TopUps';
import SaveButton from '../ui/SaveButton';

import BalanceTiles from '../home/BalanceTiles';
import FloatBalanceTile from '../home/FloatBalanceTile';
import CustomerBudgetAccountUpdateMutation from './CustomerBudgetAccountUpdateMutation'

import AppBindStyles from '../../AppBindStyles';
import { green } from '@mui/material/colors';

const abEnvironment = new AbEnvironment();

/* Need funding sources for the individual topup */
const CustomerBudgetQuery = graphql`
query CustomerBudgetQuery($accountId: UUID!) {
  accountTByAccountId(accountId: $accountId) {
    accountTByParentId {
     accountName
     logo
    }
    accountName
    contactFirstName
    contactLastName
    contactEmail
    accountType
    useBudgets
    autoTopupActive
    payFromBalance
    initialBalanceAmount
    autoTopupAmount
    autoTopupThreshhold
    rAutoTopupAmount
    rAutoTopupThreshold
    maxCharge
    budgetApprovedCount
    floatLimit
    requestedMaxCharge
    requestedFloatLimit
    rMaxCharge
    maxMonthlyBudget
    rMaxMonthlyBudget
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
  },
  sectionBlock: {
    marginBottom: 12,  
  },
  paper: {
    textAlign: 'left',
    //color: theme.palette.text.secondary,
    maxWidth: 1122,
    paddingTop: 16,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
  },
  currencyField: {
    width: 144,
  },
  currencyField2: {
    width: 176,
  },
  currencyField3: {
    width: 100,
    marginTop: 4,
    marginBottom: 4,
    marginRight: 8,
  },
  currencyLabelField: {
    width: 130,
    marginTop: 10,
    marginBottom: 4,
    // marginRight: 8,
  },
  noBudgetHolder: {
    width: 108,
    marginTop: 12,
    marginBottom: 8,
    marginRight: 8,
    textAlign: 'right',
  },
  currencyFieldSlider: {
    marginLeft: 8,
    width:64,
  },
  success: {
    backgroundColor: green[300],
  },
  error: {
    backgroundColor: theme.palette.error.light,
  },

});

function ValueLabelComponent(props) {
  const { children, open, value } = props;

  let formattedValue = Number(value).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2})

  return (
    <Tooltip open={open} enterTouchDelay={0} placement="bottom" title={formattedValue}>
      {children}
    </Tooltip>
  );
}

ValueLabelComponent.propTypes = {
  children: PropTypes.element.isRequired,
  open: PropTypes.bool.isRequired,
  value: PropTypes.number.isRequired,
};

function ValueLabelFormat(value) {
  let theVal = value;
  if (isNaN(value))
    theVal = 0;

  return Number(theVal).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 0});
}

ValueLabelFormat.propTypes = {
  value: PropTypes.number.isRequired,
};

class CustomerBudget extends Component {
  constructor(props)
  {
    super(props);
    this.state = {
      useBudgets:              null,
      autoTopupActive:         null,
      payFromBalance:          null,
      initialBalanceAmount:    null,
      rAutoTopupAmount:        null,
      rAutoTopupThreshold:     null,
      maxCharge:               null,
      floatLimit:              null,
      requestedMaxCharge:      9900,
      requestedFloatLimit:     null,
      maxMonthlyBudget:        null,
      rMaxMonthlyBudget:       null,
      rMaxMonthlyBudgetSet:    false, // Workaround to indicate if the state value is null or unset

      accountFetched: true,
      forceFetch: 0,

      saveStatus: null,
      saveMessage: null,
      saveSnackbarOpen: false,
      saveSnackbarDuration: null,

      confirmSave: false,
    };

  }

  /*
   * Invite a (customer) user to approve budgets and put a funding source on file to pay for the subscription.
   * This method will be passed to an invite component.
   */
  inviteCustomerUser = (bcUser) => {
    // If the user already exists, send them to the user edit to invite using that current user.
    // Otherwise, send to create.

    localStorage.setItem('budgetReturn', bcUser.accountId);

    if (bcUser.userid != null && bcUser.userid !== '') {
      let link = 'useredit?userId=' + bcUser.userid + '&accountId=' + bcUser.accountId;
      this.props.history.push(link);
    } else {
      let propsToCache = {'contactLastName'  : bcUser.lastname,
                          'contactFirstName' : bcUser.firstname,
                          'contactEmail'     : bcUser.email};
      localStorage.setItem('userCreateParamProps', JSON.stringify(propsToCache));
      let link = 'usercreate?accountId=' + bcUser.accountId;
      this.props.history.push(link);
    }
  }
  
  /*
   * Reinvite a customer user to approve budgets.
   * This method will be passed to an invite component.
   */
  reinviteCustomerUser = (bcUser) => {
    localStorage.setItem('budgetReturn', bcUser.accountId);
    let link = 'useredit?userId=' + bcUser.userid + '&accountId=' + bcUser.accountId;
    this.props.history.push(link);
  }

  handleRequestedFloatLimitChange  = (event, newValue) => {
    this.setState({ requestedFloatLimit: newValue});
  }

  /* Determine value to used based on state and prop values*/
  getValueForRender(state_member, prop_member, the_default)
  {
    if (state_member != null)
      return state_member;
    
    if (prop_member != null)
      return prop_member;

    return the_default;
  }

  /* Determine if a value has changed */
  hasValueChanged(state_member, prop_member) {
    if (state_member != null && state_member !== prop_member) {
      return true;
    }
    return false;
  }

  /* Determine if a value has changed */
  hasNumericValueChanged(state_member, prop_member) {
    if (state_member != null && Number(state_member) !== Number(prop_member)) {
      return true;
    }
    return false;
  }

  /* Special check for monthly budget value */
  hasMonthlyBudgetValueChanged(budgetValueSet, state_member, prop_member) {
    if (budgetValueSet) {
      if (state_member == null && prop_member != null) {
        return true;
      }

      if (state_member != null && prop_member == null) {
        return true;
      }

      if (state_member != null && prop_member != null && Number(state_member) !== Number(prop_member)) {
        return true;
      }
    }
    return false;
  }



  // Determine if budget save should be disabled
  isSaveDisabled(theAccount, isUserAppBind, considerInitial) { 
    if (isUserAppBind) {
      if (! this.hasNumericValueChanged(this.state.maxCharge,  theAccount.maxCharge) &&
          ! this.hasNumericValueChanged(this.state.floatLimit, theAccount.floatLimit) )
      {
        return true;
      }
    } else {
      if (! this.hasValueChanged       (this.state.autoTopupActive,      theAccount.autoTopupActive)      &&
          ! this.hasNumericValueChanged(this.state.rAutoTopupAmount,     theAccount.rAutoTopupAmount)     &&
          ! this.hasNumericValueChanged(this.state.rAutoTopupThreshold,  theAccount.rAutoTopupThreshold)  &&
          ! this.hasNumericValueChanged(this.state.initialBalanceAmount, theAccount.initialBalanceAmount) &&
          ! this.hasValueChanged       (this.state.useBudgets,           theAccount.useBudgets)           &&
          ! this.hasNumericValueChanged(this.state.requestedFloatLimit,  theAccount.requestedFloatLimit)  &&
          ! this.hasMonthlyBudgetValueChanged(this.state.rMaxMonthlyBudgetSet, this.state.rMaxMonthlyBudget, theAccount.rMaxMonthlyBudget) )
      {
        return true;
      }

    }

    return false;
  }

  // Determine if the change request button is disabled
  isChangeRequestDisabled(theAccount) {
    // If there are unsaved changes, disable.
    if (this.hasValueChanged       (this.state.autoTopupActive,      theAccount.autoTopupActive)      ||
        this.hasNumericValueChanged(this.state.rAutoTopupAmount,     theAccount.rAutoTopupAmount)     ||
        this.hasNumericValueChanged(this.state.rAutoTopupThreshold,  theAccount.rAutoTopupThreshold)  ||
        this.hasNumericValueChanged(this.state.initialBalanceAmount, theAccount.initialBalanceAmount) ||
        this.hasValueChanged       (this.state.useBudgets,           theAccount.useBudgets)           ||
        this.hasNumericValueChanged(this.state.requestedFloatLimit,  theAccount.requestedFloatLimit)  ||
        this.hasMonthlyBudgetValueChanged(this.state.rMaxMonthlyBudgetSet, this.state.rMaxMonthlyBudget, theAccount.rMaxMonthlyBudget) )
    {
      return true;
    }

    // If nothing relevant has changed, disable
    if (
      Number(theAccount.rAutoTopupAmount)    === Number(theAccount.autoTopupAmount) &&
      Number(theAccount.rAutoTopupThreshold) === Number(theAccount.autoTopupThreshhold) &&
      Number(theAccount.rMaxMonthlyBudget)   === Number(theAccount.maxMonthlyBudget)
    ) {
      return true;
    }

    return false;
  }

  toggleUseBudgets(ub) {
    if (ub === 0 || ub == null) {
      this.setState({ useBudgets : 1, autoTopupActive: 1, payFromBalance: 1});
    } else {
      this.setState({ useBudgets : 0, autoTopupActive: 0, payFromBalance: 0});
    }
  }

  
  goToSO(props) {
    
    let propsToCache = {
      fromRequestApproval: true,
      accountId: this.props.accountId,
      companyName: props.accountTByAccountId.accountName,
      contactLastName: props.accountTByAccountId.contactLastName, 
      contactFirstName: props.accountTByAccountId.contactFirstName, 
      contactEmail:  props.accountTByAccountId.contactEmail};
    localStorage.setItem('soCustomerParms', JSON.stringify(propsToCache));


    this.props.history.push(`/subscriptionorder`);
  }

  goToSOWithoutBudget(props) {
    
    let propsToCache = {
      accountId: this.props.accountId,
      companyName: props.accountTByAccountId.accountName,
      contactLastName: props.accountTByAccountId.contactLastName, 
      contactFirstName: props.accountTByAccountId.contactFirstName, 
      contactEmail:  props.accountTByAccountId.contactEmail};
    localStorage.setItem('soCustomerParms', JSON.stringify(propsToCache));


    this.props.history.push(`/subscriptionorder`);
  }


  render() {
    const classes = this.props.classes;

    const { logError } = this.props.auth;       
    
    let acctId = this.props.accountId;
    
    //here we need to figure out if the user is one of us but NOT masquarading.
    let isUserAppBind = (abEnvironment.getUserAttribute("role") === "appbind_appbind");

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={CustomerBudgetQuery}
        variables={{accountId: acctId, forceFetch: this.state.forceFetch}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {
            // Use this flag to defer display until all widgets are loaded
            if (!this.state.accountFetched) { 
              return (
                <div>
                  <LoadingSpinner/>
                </div>
              );
            } else {
              // Get display values from state if set, props if not
              let theAccount = props.accountTByAccountId;

              // Sub order should be shown if budgets are on
              let subOrder = true;
              let rUseBudgets = 2;
              try {
                rUseBudgets = Number.parseInt(abEnvironment.getUserAttribute('a_useBudgets'));
                if (rUseBudgets === 0) {
                  subOrder = false;
                }
              } catch (nErr) {
                // Swallow
              }

              // The budgets rendering value is:
              // - 2 if reseller budget usage is 2
              // - Otherwise, the value of the customer budget usage.
              // So what this means is that if the reseller is only allowed to use budgets (reseller 2), we don't
              // let the reseller change the customer to turn budgets on or off.  But if the reseller isn't forced to
              // use budgets, we allow the reseller to turn budgets on or off.  Practically, this means that the
              // reseller must have useBudgets of 1, because if they had useBudgets 0, they would see the ChargeLimits
              // component instead.
              //
              let useBudgetsValueForRender = 2;
              if (rUseBudgets !== 2) {
                useBudgetsValueForRender = this.getValueForRender(this.state.useBudgets, theAccount.useBudgets, 0);
              }

              let currentRequestedFloatLimitNum = this.getValueForRender(this.state.requestedFloatLimit, theAccount.requestedFloatLimit, 0);
              // let currentRequestedFloatLimit = Number(currentRequestedFloatLimitNum).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 0});

              let showInitialValue = false;
              if (props.accountTByAccountId.budgetApprovedCount === 0) {
                showInitialValue = true;
              }

              let initialBalanceAmount = getValueForRender(theAccount.initialBalanceAmount, 'Not set');

              // Auto-topup threshold
              let pendingAutoTopupThreshold = getValueForRender(theAccount.rAutoTopupThreshold, 'Not set');
              let autoTopupThreshold        = getValueForRender(theAccount.autoTopupThreshhold, 'Not set');
              let autoTopupThresholdTooltip = null;
              if (pendingAutoTopupThreshold !== 'Not set' && autoTopupThreshold !== 'Not set' && pendingAutoTopupThreshold !== autoTopupThreshold) {
                autoTopupThresholdTooltip = 'Pending change from ' + Number(autoTopupThreshold).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
              }            
  
              // Auto-topup amount
              let pendingAutoTopupAmount = getValueForRender(theAccount.rAutoTopupAmount, 'Not set');
              let autoTopupAmount        = getValueForRender(theAccount.autoTopupAmount, 'Not set');
              let autoTopupAmountTooltip = null;
              if (pendingAutoTopupAmount !== 'Not set' && autoTopupAmount !== 'Not set' && pendingAutoTopupAmount !== autoTopupAmount) {
                autoTopupAmountTooltip = 'Pending change from ' + Number(autoTopupAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
              }
  
              // Monthly limit
              let pendingMaxMonthlyBudget = getValueForRender(theAccount.rMaxMonthlyBudget, 0);
              let maxMonthlyBudget        = getValueForRender(theAccount.maxMonthlyBudget, 0);
              let maxMonthlyTooltip       = null;

              let pendingMaxMonthlyBudgetString = 'No limit';
              if (pendingMaxMonthlyBudget != null && pendingMaxMonthlyBudget > 0) {
                pendingMaxMonthlyBudgetString = pendingMaxMonthlyBudget;
              }

              let maxMonthlyBudgetString = 'No limit';
              if (maxMonthlyBudget != null && maxMonthlyBudget > 0) {
                maxMonthlyBudgetString = maxMonthlyBudget;
              }

              if (pendingMaxMonthlyBudgetString !== maxMonthlyBudgetString) {
                if (pendingMaxMonthlyBudgetString === 'No limit') {
                  maxMonthlyTooltip = 'Pending change to remove monthly spend limit (' + Number(maxMonthlyBudgetString).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
                } else {
                  if (maxMonthlyBudgetString === 'No limit') {
                    maxMonthlyTooltip = 'Pending change from no limit';
                  } else {
                    maxMonthlyTooltip = 'Pending change from ' + Number(maxMonthlyBudgetString).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                  }
                }
              }

              // Denominator for spending widget
              let monthlyDenominator = maxMonthlyBudget;
              if (monthlyDenominator === 0) {
                monthlyDenominator = null;
              }

              // SO buttons
              let soButtonsDisabled = true;
              if (useBudgetsValueForRender >= 1 && subOrder) {
                soButtonsDisabled = false;
              }

              return (
                <Grid container spacing={2} direction="row" justifyContent="flex-start">
                  <Grid item xs={12}>
                    <Grid container spacing={0} direction="column" justifyContent="flex-start">
                      <Grid item>
                        <Typography variant="h6">Customer expense wallet</Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="body2">Your customer's expense wallet is used to pay for subscriptions that you provision and bill to them.</Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {useBudgetsValueForRender <= 1 &&
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Grid container justifyContent="fkex-start" alignItems="center" spacing={2}>
                        <Grid item>
                          <Grid container spacing={0}>
                            <Grid item>
                              <Typography variant="body1" style={AppBindStyles.boldLabel}>Use expense wallet</Typography>
                            </Grid>
                            <Grid item>
                              <Tooltip placement='bottom' title="When you turn on the expense wallet feature, your customer's subscriptions will be paid for out of their wallet balance.  You can send them a subscription order to set up their wallet, and get their assent to the amount that they'll be funding.  You'll also be able to add billing for your own services.">
                                <HelpOutline fontSize="small" style={{verticalAlign: "-16%", marginLeft: '4px'}}/>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <Switch checked={useBudgetsValueForRender >= 1} 
                                  onChange={(e) => this.toggleUseBudgets(useBudgetsValueForRender)} 
                                  value={useBudgetsValueForRender}
                                  color="primary"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          {!this.isSaveDisabled(theAccount, isUserAppBind) && 
                          <SaveButton
                            snackbarOpen={this.state.saveSnackbarOpen}
                            autoHideDuration={this.state.saveSnackbarDuration}
                            onSaveClick={() => { this._onSave(theAccount, isUserAppBind);}}
                            onSnackbarClose={() => { this._onSnackbarClose(); }}
                            status={this.state.saveStatus}
                            message={this.state.saveMessage}
                          />}
                          {this.isSaveDisabled(theAccount, isUserAppBind) && 
                          <Button
                            variant="contained"
                            disabled
                          >
                            Save
                          </Button>}
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>}

                  <Grid item xs={12}>
                    <Grid container spacing={2} direction="row" justifyContent="flex-start">
                      <Grid item xs={12}>
                        <Paper className={classes.paper}>
                          <Grid container spacing={2} direction="row">
                            <Grid item xs={12}>
                              <Typography align="left" variant="h6">Balances</Typography>
                            </Grid>

                            {/* Balance widgets */}
                            <Grid item xs={12}>
                              <Grid container spacing={3} justifyContent="flex-start">
                                <Grid item>
                                  <BalanceTiles auth={this.props.auth} accountId={acctId}/>
                                </Grid>
                                <Grid item>
                                  <FloatBalanceTile auth={this.props.auth} accountId={acctId} floatLimit={currentRequestedFloatLimitNum}/>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item xs={12}>
                              <Grid container spacing={1}>
                                {/* Only show initially fund on first time use - that is, until a budget has been accepted */}
                                {showInitialValue === true &&
                                <Grid item xs={12}>
                                  <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                                    <Grid item>
                                      <Typography variant="body1" style={AppBindStyles.boldLabel}>Initially fund balance to</Typography>
                                    </Grid>
                                    <Grid item > 
                                      {initialBalanceAmount === 'Not set' &&
                                        <Typography variant="body1">{initialBalanceAmount}</Typography>
                                      }
                                      {initialBalanceAmount !== 'Not set' &&

                                          <Typography variant="body1">
                                            <NumberFormat
                                              value={initialBalanceAmount}
                                              valueIsNumericString
                                              prefix={'$'} 
                                              displayType="text"
                                              decimalScale={2}
                                              fixedDecimalScale
                                              thousandSeparator/>
                                          </Typography>

                                      }
                                    </Grid>
                                  </Grid>
                                </Grid>}

                                <Grid item xs={12}>
                                  <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                                    <Grid item>
                                      <Typography variant="body1" style={AppBindStyles.boldLabel}>When balance falls below</Typography>
                                    </Grid>
                                    <Grid item>
                                      <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                                        {autoTopupThresholdTooltip != null &&
                                        <Grid item>
                                          <Tooltip placement='bottom' title={autoTopupThresholdTooltip}>
                                            <SettingsBackupRestore 
                                            fontSize="small" 
                                            style={{verticalAlign: "-8%"}}
                                            />
                                          </Tooltip>
                                        </Grid>}
                                        <Grid item>
                                          {pendingAutoTopupThreshold === 'Not set' &&
                                            <Typography variant="body1">{pendingAutoTopupThreshold}</Typography>}
                                          {pendingAutoTopupThreshold !== 'Not set' &&
                                            <Typography variant="body1">
                                              <NumericFormat
                                                value={pendingAutoTopupThreshold}
                                                valueIsNumericString
                                                prefix={'$'} 
                                                displayType="text"
                                                decimalScale={2}
                                                fixedDecimalScale
                                                thousandSeparator/>
                                            </Typography>}
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                  <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                                    <Grid item>
                                      <Typography variant="body1" style={AppBindStyles.boldLabel}>Fund balance to</Typography>
                                    </Grid>
                                    <Grid item> 
                                      <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                                        {autoTopupAmountTooltip != null &&
                                        <Grid item>
                                          <Tooltip placement='bottom' title={autoTopupAmountTooltip}>
                                            <SettingsBackupRestore 
                                            fontSize="small" 
                                            style={{verticalAlign: "-8%"}}
                                            />
                                          </Tooltip>
                                        </Grid>}
                                        <Grid item>
                                          {pendingAutoTopupAmount === 'Not set' &&
                                            <Typography variant="body1">{pendingAutoTopupAmount}</Typography>}
                                          {pendingAutoTopupAmount !== 'Not set' &&
                                            <Typography variant="body1">
                                              <NumericFormat
                                                value={pendingAutoTopupAmount}
                                                valueIsNumericString
                                                prefix={'$'} 
                                                displayType="text"
                                                decimalScale={2}
                                                fixedDecimalScale
                                                thousandSeparator/>
                                            </Typography>}
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item xs={12}>
                              <Grid container spacing={0}>
                                <Grid item style={AppBindStyles.bottomButton}>
                                  {this.props.invitationNeeded !== true &&
                                  <Tooltip placement="bottom" title="Send a revised subscription order to your customer to request a change in the funding settings for their wallet.">
                                    <Button color="primary" variant="outlined" 
                                            disabled={soButtonsDisabled}
                                            onClick={(e) => {this.goToSO(props);}}>
                                      Request wallet change
                                    </Button>
                                  </Tooltip>}
                                  {this.props.invitationNeeded === true &&
                                  <Tooltip placement="bottom" title="Send a subscription order to your customer to request funding for their wallet.">
                                    <Button color="primary" variant="outlined"
                                            disabled={soButtonsDisabled}
                                            onClick={(e) => {this.goToSOWithoutBudget(props);}}>
                                      Invite and request wallet funding
                                    </Button>
                                  </Tooltip>}
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Grid container spacing={2} direction="row" >
                        <Grid item xs={12}>
                          <Typography align="left" variant="h6">Spending</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <SpendingWidgets accountId={acctId} spendLimit={monthlyDenominator}/>
                        </Grid>
                        <Grid item xs={12}>
                          <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                            <Grid item>
                              <Typography variant="body1" style={AppBindStyles.boldLabel}>Monthly spend limit</Typography>
                            </Grid>
                            <Grid item>
                              <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                                {maxMonthlyTooltip != null &&
                                <Grid item>
                                  <Tooltip placement='bottom' title={maxMonthlyTooltip}>
                                    <SettingsBackupRestore 
                                    fontSize="small" 
                                    style={{verticalAlign: "-8%"}}
                                    />
                                  </Tooltip>
                                </Grid>}
                                <Grid item>
                                  {pendingMaxMonthlyBudgetString === 'No limit' &&
                                  <Typography variant="body1">{pendingMaxMonthlyBudgetString}</Typography>}
                                  {pendingMaxMonthlyBudgetString !== 'No limit' &&
                                  <Typography variant="body1">
                                    <NumericFormat
                                      value={pendingMaxMonthlyBudget}
                                      valueIsNumericString
                                      prefix={'$'} 
                                      displayType="text"
                                      decimalScale={2}
                                      fixedDecimalScale
                                      thousandSeparator/>
                                  </Typography>}
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                
                  { useBudgetsValueForRender >= 1 &&
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Grid container spacing={2} direction="row" >
                        <Grid item xs={12}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Grid container spacing={0}>
                                <Grid item >
                                  <Typography align="left" variant="h6">Budget funding history</Typography>
                                </Grid>                          
                              </Grid>
                            </Grid>

                            <Grid item xs={12}>
                              <TopUps auth={this.props.auth}
                                      accountId={acctId}/>
                            </Grid>

                          </Grid>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>}
                </Grid>
              );
            }
          }
          return <div><LoadingSpinner/></div>
        }}
      />
    );
  }
  
  _onSnackbarClose() {
    this.setState( {saveSnackbarOpen: false, saveStatus: '', saveMessage: '', saveSnackbarDuration: null});
  }

  _onSave(theAccount, isUserAppBind) {
    if (this.hasValueChanged(this.state.autoTopupActive, theAccount.autoTopupActive)) {
      this.setState({confirmSave:true});
    } else {
      this._update(theAccount, isUserAppBind);
    }
  }

  _undoChangedFields(theAccount) {
    this.setState({ useBudgets: theAccount.useBudgets, autoTopupActive: theAccount.autoTopupActive, payFromBalance: theAccount.payFromBalance, 
                    initialBalanceAmount: theAccount.initialBalanceAmount, rAutoTopupThreshold: theAccount.rAutoTopupThreshold, rAutoTopupAmount: theAccount.rAutoTopupAmount, 
                    requestedFloatLimit: theAccount.requestedFloatLimit, rMaxMonthlyBudget: theAccount.rMaxMonthlyBudget });
  }

/*
   * Called to update fields
   */
  _update(theAccount, isUserAppBind) {

    const { callPartnerApi } = this.props.auth;

    this.setState({ saveStatus: 'saving' });
    const { useBudgets, autoTopupActive, payFromBalance, initialBalanceAmount, rAutoTopupThreshold, rAutoTopupAmount, maxCharge, floatLimit, requestedMaxCharge, requestedFloatLimit, rMaxMonthlyBudget, rMaxMonthlyBudgetSet } = this.state;
    let accountId = this.props.accountId;
    let forceFetch = this.state.forceFetch;
    forceFetch++;

    if (isUserAppBind) {
      let newRequestMaxCharge    = requestedMaxCharge;
      let newRequestedFloatLimit = requestedFloatLimit;

      if (parseFloat(newRequestMaxCharge) > parseFloat(maxCharge)) {
        newRequestMaxCharge = maxCharge;
      }
      if (parseFloat(newRequestedFloatLimit) > parseFloat(floatLimit)) {
        newRequestedFloatLimit = floatLimit;
      }

      /*
      CustomerBudgetAccountUpdateMutation.commit(
        accountId, useBudgets, autoTopupActive, payFromBalance, 
        initialBalanceAmount, rAutoTopupThreshold, rAutoTopupAmount, rMaxMonthlyBudget, rMaxMonthlyBudgetSet,
        maxCharge, floatLimit, requestedMaxCharge, requestedFloatLimit, rMaxCharge, latestBudgetApproved)

        accountId, useBudgets, autoTopupActive, payFromBalance, 
        initialBalanceAmount, autoTopupThreshhold, autoTopupAmount, maxMonthlyBudget, maxMonthlyBudgetSet,
        rAutoTopupThreshold, rAutoTopupAmount, rMaxMonthlyBudget, rMaxMonthlyBudgetSet,
        maxCharge, floatLimit, requestedMaxCharge, requestedFloatLimit, rMaxCharge, latestBudgetApproved)
        */

      CustomerBudgetAccountUpdateMutation.commit(
        accountId, null, null, null, 
        null, null, null, null, false,
        null, null, null, false,
        maxCharge, floatLimit, requestedMaxCharge, requestedFloatLimit, null, null)
      .then(response => {
        // console.log('Account updated')
        this.setState({ forceFetch: forceFetch, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Updates saved', saveSnackbarDuration: 3000});
        
      })
      .catch(error => {
        // console.log('Got error: ' + error);
        this.setState({ saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Problem saving', saveSnackbarDuration: null});
      });
    } else {
      let latestBudgetApproved = null;

      // Unset latestBudgetApproved if there is a change that requires customer approval.
      if (
        (useBudgets          != null && useBudgets          >= 1) ||
        (rAutoTopupThreshold != null && rAutoTopupThreshold !== theAccount.autoTopupThreshhold) ||
        (rAutoTopupAmount    != null && rAutoTopupAmount    !== theAccount.autoTopupAmount) ||
        (rMaxMonthlyBudget   != null && rMaxMonthlyBudget   !== theAccount.maxMonthlyBudget)
      ) {
        latestBudgetApproved = 0;
      }

      // Need to set actual values on initialBalanceAmount, rAutoTopupThreshold, rAutoTopupAmount
      // - if the user accepts the defaults, state values will be null, so need to set the defaults
      //
      let t_initialBalanceAmount = initialBalanceAmount;
      if (t_initialBalanceAmount == null) {
        t_initialBalanceAmount = theAccount.initialBalanceAmount;
        if (t_initialBalanceAmount == null && theAccount.budgetApprovedCount === 0) {
          t_initialBalanceAmount = 500.00;
        }
      }

      let t_rAutoTopupThreshold = rAutoTopupThreshold;
      if (t_rAutoTopupThreshold == null) {
        t_rAutoTopupThreshold = theAccount.rAutoTopupThreshold;
        if (t_rAutoTopupThreshold == null) {
          t_rAutoTopupThreshold = 200.00;
        }
      }

      let t_rAutoTopupAmount = rAutoTopupAmount;
      if (t_rAutoTopupAmount == null) {
        t_rAutoTopupAmount = theAccount.rAutoTopupAmount;
        if (t_rAutoTopupAmount == null) {
          t_rAutoTopupAmount = 500.00;
        }
      }

      // We are using requested max charge for rMaxCharge.  But if this hasn't been changed, the
      // state value for requested max charge will be null.  So look this up.
      // TODO - this should be cleaned up.  But for now, this is intentional - we are using requestedMaxCharge
      // to set rMaxCharge.
      let t_rMaxCharge = requestedMaxCharge;
      if (t_rMaxCharge == null) {
        t_rMaxCharge = theAccount.requestedMaxCharge;
      }

      //if the reseller is setting useBudgets off and it was on before, we need to set the latest budget approved flag
      //we will also attach any bind cards that were waiting on approval.  In other words, the reseller could have 
      //created subscriptions using SO that are waiting on approval.  If they toggle the customer to not use budgets anymore
      //we should release those subscriptions as they are no longer waiting on approval.
      //NOTICE this is inside the subOrder feature flag as we only want to do this if using subscription orders.
      let releaseBindCards = false;
      if (useBudgets === 0 && theAccount.useBudgets >= 1) {
        latestBudgetApproved = 1;
        releaseBindCards = true;
      }

      // For now, set both requestedMaxCharge and rMaxCharge to the same value
      CustomerBudgetAccountUpdateMutation.commit(
        accountId, useBudgets, autoTopupActive, payFromBalance, 
        t_initialBalanceAmount, null, null, null, false,
        t_rAutoTopupThreshold, t_rAutoTopupAmount, rMaxMonthlyBudget, rMaxMonthlyBudgetSet,
        null, null, requestedMaxCharge, requestedFloatLimit, t_rMaxCharge, latestBudgetApproved)
      .then(response => {
        if (releaseBindCards) {
          // Attach bindbards for any subscriptions created.
          let aPjwt = abEnvironment.getPjwt();
          let payload = {
            operation: "checkAndAttachBindcards",
            params: {
              pJwt:      aPjwt,
              accountId: accountId
            }
          };
            return callPartnerApi('appbind/generic',
                                'POST',
                                payload);

        } else {
          return null;
        }
      })
      .then(response => {
        // console.log('Account updated')
        this.setState({ forceFetch: forceFetch, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Updates saved', saveSnackbarDuration: 3000});  
      })
      .catch(error => {
        // console.log('Got error: ' + error);
        this.setState({ saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Problem saving', saveSnackbarDuration: null});
      });
    }
  }
}
  
CustomerBudget.propTypes = {
    classes: PropTypes.object.isRequired,
  };
      
export default withStyles(styleSheet)(CustomerBudget);
